import {
  ErrorResultPage,
  ErrorStatistics,
  SubmissionResultPage,
  SubmissionStatistics,
  SearchHistoryResultPage,
  SearchHistoryStatistics,
  Feedback,
  Vineyard,
  VineyardsResultPage,
  VineyardSubmission,
} from "./models";
import { SortOrder } from "../components/table/table";
import { AxiosError } from "axios";
import { LocalDateTime } from "@js-joda/core";
import fileDownload from "js-file-download";
import apiClient from "./api-client";

interface ErrorResponse {
  error?: string; // Matches potential backend error response
}
export interface VineyardsQuery {
  searchText?: string;
  searchValue?: string;
  field: string;
  order: SortOrder;
  pageSize: number;
  pageNumber: number;
}

export interface AdvancedVineyardsQuery {
  /**
   * setting boolean that search was selected
   * this is to avoid calling advanced search
   */
  search: boolean;
  /**
   * The country where the vineyards are located.
   * This is required to narrow down the search.
   */
  country: string;
  /**
   * The region within the country.
   * This is required to narrow down the search.
   */
  region: string;
  /**
   * The region within the country.
   * This is required to narrow down the search.
   */
  subRegion: string;
  /**
   * The sub-sub-region within the sub-region.
   * This is optional and can be used to further narrow down the search.
   */
  subSubRegion: string;
  /**
   * If true, only vineyards with coordinates will be returned.
   * If false, or undefined, both will be returned.
   */
  hasCoordinates?: boolean;
  /**
   * If true, only vineyards with pictures will be returned.
   * If false, or undefined, both will be returned.
   */
  hasPictures?: boolean;
  /**
   * The producer of the vineyards.
   * If not provided, all producers will be included.
   */
  producer?: string;
  /**
   * An array of size ranges to filter the vineyards by.
   * Each range is a string in the format "min-max" (e.g., "10-20").
   * If not provided, all size ranges will be included.
   */
  sizeRange?: string;
  /**
   * An array of elevation ranges to filter the vineyards by.
   * Each range is a string in the format "min-max" (e.g., "100-200").
   * If not provided, all elevation ranges will be included.
   */
  elevationRange?: string;
  /**
   * An array of grape varieties to filter the vineyards by.
   * If not provided, all varieties will be included.
   */
  varieties: string[];
  /**
   * An array of age ranges to filter the vineyards by.
   * Each range is a string in the format "min-max" (e.g., "10-20").
   * If not provided, all age ranges will be included.
   */
  ageRange: string[];
  /**
   * An array of boolean values indicating whether the vineyard is own-rooted.
   * If not provided, all vineyards will be included.
   */
  isOwnRooted: string[];
  field: string;
  order: SortOrder;
  pageSize: number;
  pageNumber: number;
}

interface VineyardsQueryByAge {
  startYear: number;
  endYear: number;
  field: string;
  order: SortOrder;
  pageSize: number;
  pageNumber: number;
}

interface VineyardsQueryByCountry {
  country: string;
  field: string;
  order: SortOrder;
  pageSize: number;
  pageNumber: number;
}

interface VineyardsQueryByVariety {
  variety: string;
  field: string;
  order: SortOrder;
  pageSize: number;
  pageNumber: number;
}

export async function getVineyards(
  query: VineyardsQuery
): Promise<VineyardsResultPage> {
  try {
    const result = await apiClient.get("/api/vineyards", {
      params: query,
    });
    return result.data;
  } catch (e) {
    console.error("getVineyards query failed:", e);
  }
  return { content: [], totalElements: 0, totalPages: 0 };
}

export async function getAdvancedVineyards(
  params: AdvancedVineyardsQuery
): Promise<VineyardsResultPage> {
  try {
    const sp = new URLSearchParams();
    //sp.append("searched" , params.searched?.toString() ?? "false");
    sp.append("search",    params.search?.toString() ?? "false");
    sp.append("country",    params.country);
    sp.append("region",     params.region);
    sp.append("subRegion",  params.subRegion);
    sp.append("subSubRegion", params.subSubRegion);
    sp.append("hasCoordinates", params.hasCoordinates?.toString() ?? "false");
    sp.append("hasPictures", params.hasPictures?.toString() ?? "false");
    sp.append("producer",  params.producer ?? "");
    sp.append("sizeRange",  params.sizeRange ?? "");
    sp.append("elevationRange",  params.elevationRange ?? "");
    params.varieties?.forEach(v => sp.append("varieties", v));
    params.ageRange?.forEach(a => sp.append("ageRange",  a));
    params.isOwnRooted?.forEach(o => sp.append("isOwnRooted", o));
    sp.append("field",      params.field);
    sp.append("order",      params.order);
    sp.append("pageSize",   params.pageSize.toString());
    sp.append("pageNumber", params.pageNumber.toString());

    const url = `/api/advanced-vineyards?${sp.toString()}`;
    const result = await apiClient.get<VineyardsResultPage>(url);
    
    return result.data;
  } catch (e) {
    console.error("getAdvancedVineyards query failed:", e);
  }
  return { content: [], 
           totalElements: 0, 
           totalPages: 0 
         };
}

export async function getAdvancedVineyardsCount(
  params: AdvancedVineyardsQuery
): Promise<number> {
  try {
    const sp = new URLSearchParams();
    //sp.append("searched" , params.searched?.toString() ?? "false");
    sp.append("search",    params.search?.toString() ?? "false");
    sp.append("country",    params.country);
    sp.append("region",     params.region);
    sp.append("subRegion",  params.subRegion);
    sp.append("subSubRegion", params.subSubRegion);
    sp.append("hasCoordinates", params.hasCoordinates?.toString() ?? "false");
    sp.append("hasPictures", params.hasPictures?.toString() ?? "false");
    sp.append("producer",  params.producer ?? "");
    sp.append("sizeRange",  params.sizeRange ?? "");
    sp.append("elevationRange",  params.elevationRange ?? "");
    params.varieties?.forEach(v => sp.append("varieties", v));
    params.ageRange?.forEach(a => sp.append("ageRange",  a));
    params.isOwnRooted?.forEach(o => sp.append("isOwnRooted", o));
    sp.append("field",      params.field);
    sp.append("order",      params.order);
    sp.append("pageSize",   params.pageSize.toString());
    sp.append("pageNumber", params.pageNumber.toString());

    const url = `/api/advanced-vineyards-count?${sp.toString()}`;
    const result = await apiClient.get<number>(url);

    return result.data;
  } catch (e) {
    console.error("getAdvancedVineyardsCount query failed:", e);
  }
  return 0;
}

export async function getVineyardsByAge(
  query: VineyardsQuery
): Promise<VineyardsResultPage> {
  let startYear = 0;
  let endYear = 0;
  if (query.searchValue === "155+") {
    startYear = 1000;
    endYear = LocalDateTime.now().minusYears(155).year();
  } else if (query.searchValue) {
    const [min, max] = query.searchValue.split("-");
    startYear = LocalDateTime.now().minusYears(+max).year();
    endYear = LocalDateTime.now().minusYears(+min).year();
  }

  const params: VineyardsQueryByAge = {
    startYear,
    endYear,
    field: query.field,
    order: query.order,
    pageSize: query.pageSize,
    pageNumber: query.pageNumber,
  };
  try {
    const result = await apiClient.get("/api/vineyards/yearRange", {
      params,
    });
    return result.data;
  } catch (e) {
    console.error("getVineyards query failed:", e);
  }
  return { content: [], totalElements: 0, totalPages: 0 };
}

export async function getVineyardsByCountry(
  query: VineyardsQuery
): Promise<VineyardsResultPage> {
  let country = query.searchValue ?? "";

  const params: VineyardsQueryByCountry = {
    country: country,
    field: query.field,
    order: query.order,
    pageSize: query.pageSize,
    pageNumber: query.pageNumber,
  };
  try {
    const result = await apiClient.get("/api/vineyards/country", {
      params,
    });
    return result.data;
  } catch (e) {
    console.error("getVineyards query failed:", e);
  }
  return { content: [], totalElements: 0, totalPages: 0 };
}

export async function getVineyardsByVariety(
  query: VineyardsQuery
): Promise<VineyardsResultPage> {
  let variety = query.searchValue ?? "";

  const params: VineyardsQueryByVariety = {
    variety: variety,
    field: query.field,
    order: query.order,
    pageSize: query.pageSize,
    pageNumber: query.pageNumber,
  };
  try {
    const result = await apiClient.get("/api/vineyards/variety", {
      params,
    });
    return result.data;
  } catch (e) {
    console.error("getVineyards query failed:", e);
  }
  return { content: [], totalElements: 0, totalPages: 0 };
}

export async function getVineyard(composite: string): Promise<Vineyard | undefined> {
  try {
    const result = await apiClient.get(`/api/vineyards/lookup/${composite}`);
    return result.data;
  } catch (e) {
    console.error("getVineyard query failed:", e);
  }
  return undefined;
}

export async function getCountries(): Promise<string[]> {
  try {
    const result = await apiClient.get("/api/vineyards/countries");
    return result.data.filter((it: string) => !!it);
  } catch (e) {
    console.error("could not get countries", e);
  }
  return [];
}

export async function getRegions(country?: string): Promise<string[]> {
  try {
    const result = await apiClient.get("/api/vineyards/regions", {
      params: country ? { country } : {},
    });
    return result.data.filter((it: string) => !!it);
  } catch (e) {
    console.error("could not get regions", e);
  }
  return [];
}

export async function getSubRegions(region?: string): Promise<string[]> {
  try {
    const result = await apiClient.get("/api/vineyards/sub-regions", {
      params: region ? { region } : {},
    });
    return result.data.filter((it: string) => !!it);
  } catch (e) {
    console.error("could not get sub-regions", e);
  }
  return [];
}

export async function getSubSubRegions(subregion?: string): Promise<string[]> {
  try {
    const result = await apiClient.get("/api/vineyards/sub-subregions", {
      params: subregion ? { subregion } : {},
    });
    return result.data.filter((it: string) => !!it);
  } catch (e) {
    console.error("could not get sub-subregions", e);
  }
  return [];
}

export async function getVarieties(): Promise<string[]> {
  try {
    const result = await apiClient.get("/api/vineyards/varieties");
    return result.data.filter((it: string) => !!it)
  } catch (e) {
    console.error("could not get cultivars", e);
  }
  return [];
}

export async function getLocationVarieties(location?: string, locationType?: string): Promise<string[]> {
  try {
    console.log(`Fetching varieties for location: ${location}, locationType: ${locationType}`);
    const response = await apiClient.get<string[] | ErrorResponse>(
      "/api/vineyards/varieties",
      {
        params: location ? { location, locationType } : {},
      }
    );

    // Check if response is an error object
    if ("error" in response.data) {
      throw new Error(response.data.error || "Failed to fetch varieties");
    }
    // Ensure response is string[] and filter out any falsy values (optional, if backend guarantees clean data)
    return Array.isArray(response.data) ? response.data.filter((item) => !!item) : [];
  } catch (error) {
    const errorMessage = error instanceof AxiosError 
      ? `Failed to fetch varieties: ${error.response?.data?.error || error.message}`
      : `Failed to fetch varieties: ${String(error)}`;
    console.error(errorMessage);
    throw new Error(errorMessage); // Propagate error for UI handling
  }
}

export async function getDatabaseInfo(): Promise<string> {
  try {
    const result = await apiClient.get("/api/databaseInfo");
    return result.data;
  } catch (e) {
    console.error("could not get database info:", e);
  }
  return "";
}


export async function getLastUpdated(): Promise<string> {
  try {
    const result = await apiClient.get("/api/vineyards/lastUpdated");
    return result.data;
  } catch (e) {
    console.error("could not get last updated:", e);
  }
  return "";
}

export async function getTotalVineyards(): Promise<string> {
  try {
    const result = await apiClient.get("/api/vineyards/totalVineyards");
    return result.data;
  } catch (e) {
    console.error("could not get last updated:", e);
  }
  return "";
}

export async function submitVineyard(vineyard: VineyardSubmission) {
  try {
    const result = await apiClient.post("/api/submission", vineyard);
    return result.data;
  } catch (e) {
    console.error("could not submit vineyard:", e);
  }
  return "";
}

export async function submitFeedback(feedback: Feedback): Promise<String> {
  try {
    const result = await apiClient.post("/api/feedback", feedback);
    return result.data;
  } catch (e) {
    console.error("could not submit feedback:", e);
  }
  return "";
}

export async function loginUser(
  username: string,
  password: string
): Promise<string> {
  try {
    const result = await apiClient.post("/api/authenticate", {
      username,
      password,
    });
    return result.data;
  } catch (e) {
    console.error("authentication call failed:", e);
    return "";
  }
}

export async function uploadDatabase(file: File) {
  console.log("File size (in bytes):", file.size);

  try {
    const formData = new FormData();
    formData.append("file", file);

    const result = await apiClient.post("/api/vineyards/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      timeout: 300000,
    });

    return result.data;
  } catch (e) {
    if (e instanceof AxiosError) {
      console.error("Axios error:", e.message);
      console.error("Config:", e.config);
      if (e.code) console.error("Error Code:", e.code);
      if (e.response) {
        console.error("Response Data:", e.response.data);
        console.error("Response Status:", e.response.status);
        console.error("Response Headers:", e.response.headers);
      }
    } else {
      console.error("Non-Axios error:", e);
    }
    throw e;
  }
}


export async function downloadDatabase() {
  try {
    const result = await apiClient.get("/api/downloadTdf", {
      responseType: "blob",
    });
    fileDownload(result.data, "old-vine-registry.tsv");
  } catch (e) {
    console.error("could not download database TSV file:", e);
  }
}

export async function downloadSynonyms() {
  try {
    const result = await apiClient.get("/api/synonyms", {
      responseType: "blob",
    });
    fileDownload(result.data, "synonyms.tsv");
  } catch (e) {
    console.error("could not download synonym TSV file:", e);
  }
}

export async function uploadSynonyms(file: File) {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const result = await apiClient.post("/api/synonyms/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return result.data;
  } catch (e) {
    if (e instanceof AxiosError) {
      console.error("Axios error:", e.message);
      console.error("Config:", e.config);
      if (e.code) console.error("Error Code:", e.code);
      if (e.response) {
        console.error("Response Data:", e.response.data);
        console.error("Response Status:", e.response.status);
        console.error("Response Headers:", e.response.headers);
      }
    } else {
      console.error("Non-Axios error:", e);
    }
    throw e;
  }
}

export async function downloadLocations() {
  try {
    const result = await apiClient.get("/api/locations", {
      responseType: "blob",
    });
    fileDownload(result.data, "locations.tsv");
  } catch (e) {
    console.error("could not download synonym TSV file:", e);
  }
}

export async function uploadLocations(file: File) {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const result = await apiClient.post("/api/locations/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return result.data;
  } catch (e) {
    if (e instanceof AxiosError) {
      console.error("Axios error:", e.message);
      console.error("Config:", e.config);
      if (e.code) console.error("Error Code:", e.code);
      if (e.response) {
        console.error("Response Data:", e.response.data);
        console.error("Response Status:", e.response.status);
        console.error("Response Headers:", e.response.headers);
      }
    } else {
      console.error("Non-Axios error:", e);
    }
    throw e;
  }
}

export async function downloadSearchHistory() {
  try {
    const result = await apiClient.get("/api/downloadSearchHistoryTdf", {
      responseType: "blob",
    });
    fileDownload(result.data, "searchhistory.tsv");
  } catch (e) {
    console.error("could not download synonym TSV file:", e);
  }
}

export interface ResultsQuery {
  order: string;
  pageNumber: number;
  pageSize: number;
}

export async function getErrors(
  query: ResultsQuery
): Promise<ErrorResultPage> {
  try {
    const result = await apiClient.get("/api/errors", { params: query });
    return result.data;
  } catch (e) {
    console.error("getErrors() query failed:", e);
  }
  return { content: [], totalElements: 0, totalPages: 0 };
}

export async function getSubmissions(
  query: ResultsQuery
): Promise<SubmissionResultPage> {
  try {
    const result = await apiClient.get("/api/submissions", { params: query });
    return result.data;
  } catch (e) {
    console.error("getSubmissions() query failed:", e);
  }
  return { content: [], totalElements: 0, totalPages: 0 };
}

export async function getSearchHistory(
  query: ResultsQuery
): Promise<SearchHistoryResultPage> {
  try {
    const result = await apiClient.get("/api/search-history", { params: query });
    return result.data;
  } catch (e) {
    console.error("getSearchHistory() query failed:", e);
  }
  return { content: [], totalElements: 0, totalPages: 0 };
}

export async function fetchErrorStatistics(): Promise<ErrorStatistics> {
  const result = await apiClient.get("/api/errors/statistics");
  return result.data;
}

export async function fetchSubmissionStatistics(): Promise<SubmissionStatistics> {
  const result = await apiClient.get("/api/submissions/statistics");
  return result.data;
}

export async function fetchSearchHistoryStatistics(): Promise<SearchHistoryStatistics> {
  const result = await apiClient.get("/api/search-history/statistics");
  return result.data;
}